import React, { useEffect, useState } from "react";
import { useModalFormDetails } from "../../hooks/use-open-modal-form-details";
import ModalGeneral from "../Modals/ModalGeneral";
import { cn } from "../../lib/utils";
import Quotes from "../../Screens/Quotes";
import { BASE_URL, QUOTES } from "../../config/api.config";
import { useTokenStore } from "../../hooks/useTokenStore";
import { useQuery } from "@tanstack/react-query";
import { FormTypes, QuoteTypes } from "src/types";
import FormDetailsQuestionsRow from "./FormDetailsQuestionsRow";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import Papa from "papaparse";
import { PiExport } from "react-icons/pi";

const exportToCSV = (quote: QuoteTypes, formDetails: FormTypes[]) => {
  const headers = [
    { label: "Quote ID", key: "id" },
    { label: "Address", key: "address" },
    { label: "Address Line 2", key: "address_line2" },
    { label: "City", key: "city_town" },
    { label: "Country Code", key: "country_code" },
    { label: "Email", key: "email" },
    { label: "First name", key: "first_name" },
    { label: "Last name", key: "last_name" },
    { label: "Phone number", key: "phone_number" },
    { label: "State", key: "state_region_province" },
    { label: "Mail Sent", key: "date_mail_sent" },
    { label: "ZipCode", key: "zip_code" },
    { label: "Price", key: "price" },
    ...formDetails.map((form, index) => ({
      key: `Question ${index + 1}`,
      label: `Question ${index + 1}`,
    })),
    ...formDetails.map((form, index) => ({
      key: `Response ${index + 1}`,
      label: `Response ${index + 1}`,
    })),
    // { label: "Form Question", key: "form_question" },
    // { label: "Form Response", key: "form_response" },
  ];

  const data = [
    {
      ...quote,
      ...formDetails.reduce((acc: any, form, index) => {
        acc[`Question ${index + 1}`] = form.text;
        acc[`Response ${index + 1}`] = form.option_text;
        return acc;
      }, {}),
      // form_question: formDetails.map((form) => form.text).join(", "),
      // form_response: formDetails.map((form) => form.option_text).join(", "),
    },
  ];

  const csv = Papa.unparse(
    {
      fields: headers.map((header) => header.key),
      data: data,
    },
    {
      header: true,
    }
  );

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${quote.id}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const ModalRowFormDetails = () => {
  const modalFormDetails = useModalFormDetails();
  const [showOption, setShowOption] = useState("details");
  const token = useTokenStore((s) => s.token);

  const getQuoteForms = async () => {
    const data = await fetch(
      `${BASE_URL}${QUOTES}?id=${modalFormDetails.quote?.id}`,
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    )
      .then((res) => res.json())
      .catch((err) => {
        console.log(err);
      });
    return data.result;
  };

  const { data: quotesDataForm, isLoading: isLoadingQuotesForm } = useQuery({
    queryKey: ["quote_form", modalFormDetails.quote?.id],
    queryFn: getQuoteForms,
    enabled: modalFormDetails.isOpen,
  });

  const handleExport = () => {
    if (modalFormDetails.quote?.id && quotesDataForm?.form) {
      exportToCSV(modalFormDetails.quote, quotesDataForm.form);
    }
  };

  return (
    <ModalGeneral
      title="Form Details"
      showModal={modalFormDetails.isOpen}
      closeModal={modalFormDetails.onClose}
    >
      <div className="p-4 ">
        <div className="border-b-2 mb-4 flex items-center  ">
          <h1
            onClick={() => setShowOption("details")}
            className={cn(
              "text-sm    pb-2 px-4 cursor-pointer",
              showOption === "details" && "border-black border-b"
            )}
          >
            Details
          </h1>
          <h1
            onClick={() => setShowOption("address")}
            className={cn(
              "text-sm  pb-2 px-4 cursor-pointer",
              showOption === "address" && "border-black border-b"
            )}
          >
            Address
          </h1>
          <h1
            onClick={() => setShowOption("options")}
            className={cn(
              "text-sm    pb-2 px-4 cursor-pointer",
              showOption === "options" && "border-black border-b"
            )}
          >
            Accesories prices
          </h1>
          <h1
            onClick={() => setShowOption("form")}
            className={cn(
              "text-sm    pb-2 px-4 cursor-pointer",
              showOption === "form" && "border-black border-b"
            )}
          >
            Form Responses
          </h1>
          <h1
            onClick={() => setShowOption("mailStatus")}
            className={cn(
              "text-sm    pb-2 px-4 cursor-pointer",
              showOption === "mailStatus" && "border-black border-b"
            )}
          >
            Mail Status
          </h1>
        </div>

        <div
          className={cn(
            "transition-all duration-300 transform",
            showOption === "details"
              ? "opacity-100 scale-100"
              : "opacity-0 scale-95 hidden"
          )}
        >
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Score</p>
            <p className="text-sm">${quotesDataForm?.quotes[0].score}</p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Branch</p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].branch ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Fitting price</p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].fitting_price ?? 0.0}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Main part No</p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].main_part_no ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Main part price</p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].main_part_price ?? 0.0}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Vehicle</p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].vehicle ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Vehicle Year</p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].year ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Premium Fiberglass Canopy</p>
            <p className="text-sm">None</p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Baseline Fiberglass Canopy</p>
            <p className="text-sm">None</p>
          </div>
        </div>
        <div
          className={cn(
            "flex-col items-start w-full hidden transition-all duration-200 ease-in-out",
            showOption === "address" && "block"
          )}
        >
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Address</p>
            <p className="text-sm">{quotesDataForm?.quotes[0].address}</p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Address Line 2</p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].address_line2 ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">City Town</p>
            <p className="text-sm">{quotesDataForm?.quotes[0].city_town}</p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Country </p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].country_code ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Province </p>
            <p className="text-sm">
              {quotesDataForm?.quotes[0].state_region_province ?? "None"}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500">Zip Code </p>
            <p className="text-sm">{quotesDataForm?.quotes[0].zip_code}</p>
          </div>
        </div>
        <div
          className={cn(
            " flex-col items-start w-full hidden transition-all duration-200 ease-in-out",
            showOption === "options" && "block"
          )}
        >
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize ">
              {quotesDataForm?.quotes[0].acc_part_no1 ?? "Accessory Part 1"}{" "}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no1}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no2 ?? "Accessory Part 2"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no2}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no3 ?? "Accessory Part 3"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no3}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no4 ?? "Accessory Part 4"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no4}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no5 ?? "Accessory Part 5"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no5}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no6 ?? "Accessory Part 6"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no6}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no7 ?? "Accessory Part 7"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no7}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1 ">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no8 ?? "Accessory Part 8"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no8}
            </p>
          </div>
          <div className="flex items-center justify-between w-full py-1 ">
            <p className="text-sm text-gray-500 capitalize">
              {quotesDataForm?.quotes[0].acc_part_no9 ?? "Accessory Part 9"}
            </p>
            <p className="text-sm">
              ${quotesDataForm?.quotes[0].acc_price_no9}
            </p>
          </div>
        </div>
        <div
          className={cn(
            " flex-col items-start w-full hidden transition-all duration-200 ease-in-out h-[350px] overflow-y-auto",
            showOption === "form" && "block"
          )}
        >
          <div>
            <div className="flex items-center justify-end">
              <div className="flex justify-end ">
                <button
                  onClick={handleExport}
                  className="bg-gray-900 hover:bg-gray-700 transition duration-300 ease-in-out text-white flex items-center space-x-2 px-4 py-2 rounded"
                >
                  <PiExport />
                  <p>Export to CSV</p>
                </button>
              </div>
            </div>
            {quotesDataForm?.form.map((form: FormTypes, i: number) => (
              <div key={form.id} className="flex flex-col py-2 border-b pb-4">
                <label className="text-sm font-medium text-gray-500">
                  Question {i + 1}
                </label>
                <h3 className="font-medium items-center justify-between text-gray-600 bg-gray-100 flex px-2 rounded mt-1 py-2">
                  {form.text}
                  <div className="hover:bg-gray-200 h-7 w-7 transition  flex items-center justify-center rounded-full cursor-pointer"></div>
                </h3>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Response
                  </label>

                  <h3 className="font-medium items-center justify-between text-gray-600 bg-blue-50 flex px-2 rounded mt-1 py-2">
                    {form.option_text}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className={cn(
            " flex-col items-start w-full hidden transition-all duration-200 ease-in-out h-[350px] overflow-y-auto",
            showOption === "mailStatus" && "block"
          )}
        >
          <div className="flex flex-col  p-4 space-y-2">
            {quotesDataForm?.quotesMailHistory.length === 0 ? (
              <div>
                <p className="text-sm font-medium text-gray-500">No info</p>
              </div>
            ) : (
              <div className="flex flex-col  space-y-2">
                <div className="flex items-center gap-4">
                  {quotesDataForm?.quotesMailHistory?.[0].email}{" "}
                  <IoCheckmarkDoneOutline size={20} color="green" />
                </div>
                <div className="flex items-center gap-4">
                  <p>Error log: </p>
                  {quotesDataForm?.quotesMailHistory?.[0].error_log === null ? (
                    <IoCheckmarkDoneOutline size={20} color="green" />
                  ) : (
                    quotesDataForm?.quotesMailHistory?.[0].error_log
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <p>Sent status: </p>
                  {quotesDataForm?.quotesMailHistory?.[0].sent_result ===
                  "1" ? (
                    <IoCheckmarkDoneOutline size={20} color="green" />
                  ) : (
                    <>
                      <span>ERROR</span>
                      <button
                        // onClick={handleSendAgain}
                        className="ml-2 bg-[#dc2626] text-white px-4 py-1 rounded"
                      >
                        Send Again
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalGeneral>
  );
};

export default ModalRowFormDetails;
